/**
 * Gardener specific to organizations. Note here the option called "section"
 * doesn't indicate the type of the tree. Instead it tells us which icons to use
 * for each organization. For example if the section is documents, the icons for
 * each organization should represent whether it has documents within it.
 */

import Backbone from 'backbone'

import _ from 'lodash'
import $ from 'jquery'
import { Transform } from 'stream'
import Gardener from 'scoreboard/components/gardener'
import paste from 'scoreboard/components/paste/paste'
import requiresOrg from 'scoreboard/components/requires-org'
import * as moveRules from './organization-move-rules'
import appDisplay from 'scoreboard/components/app-display'
import ErrorView from 'scoreboard/components/error-view'
import sections from 'scoreboard/components/sections'
import projects from 'scoreboard/projects/projects'
import orgHelper from './org-helper'
import TreeStream from 'scoreboard/components/tree-stream'
import renderProjectDropdown from 'scoreboard/projects/render-project-dropdown'
import pendingDeletionTransform from 'scoreboard/components/pending-deletion-transform'
import i18n from 'scoreboard/components/i18n'
import * as AddTypes from 'scoreboard/components/add-types'
const { CHILD } = AddTypes
import noOrgsViewable from './no-orgs-viewable.hbs'

/**
 *
 * @param {Boolean} [options.showProjectSelector=false]
 *
 * @emits projectId
 * 	when the projectId is changed
 *
 * The gardener emits many events too, see gardener.js#_bindEvents
 */
var OrganizationsGardener = Gardener.extend({

	forest: false,

	render() {
		Gardener.prototype.render.apply(this, arguments)
		this.showProjectSelector = this.options.showProjectSelector
		// render the project selector if there should be one here
		if (projects.isVisible() && this.showProjectSelector) {
			this.$('.finder').prepend('<div class="project-selection"><select class="project small"></select></div>')
			const select = this.$('.project')
			renderProjectDropdown(select, {
				projectId: this.options.projectId
			})
			select.removeClass('is-not-displayed')

			select.on('change', e => {
				this._onChangeProject(e)
			})
		}
		return this
	},

	_onChangeProject: function(e) {
		this.trigger('projectId', $(e.target).val())
	},

	_resolveUrlSection (section) {
		if (!section || !requiresOrg(section)) {
			section = 'scorecards'
		}
		return '/' + sections.toUrl(section)
	},

	readyToStream: function(options={}) {
		// #25537 - No project, no orgs; wait until a project is selected
		return !_.isUndefined((_.defaultTo(options.projectId, projects.id)))
	},

	_url: function(options) {
		options = options || this.options
		// ProjectId is mandatory, if it's not passed use the global project
		options.projectId = _.defaultTo(options.projectId, projects.id)
		const section = this._resolveUrlSection(_.get(this.options, 'section'))
		let url = 'api/organizations' + section
		const params = _.pick(options, 'projectId', 'permissions', 'copyInfo',
			'itemTypes', 'descendants', 'ordered')
		if (!_.isEmpty(params)) {
			url += '?' + $.param(params)
		}
		return url
	},

	_bindEvents: function() {
		let eventsBound = this._eventsBound
		Gardener.prototype._bindEvents.apply(this, arguments)

		// don't bind to global events more than once
		if (eventsBound) {
			return
		}

		this.listenTo(Backbone, 'empty:tree', this._unfill)
		this.listenTo(Backbone, 'change:root', this._changeRoot.bind(this))
		this.listenTo(Backbone, 'load:orgTree', this._loadOrgTree.bind(this))
		this.on('end', () => this.$el.trigger('end'))
	},

	_iconTransform: function() {
		var self = this
		var icons = new Transform({ objectMode: true })
		this.viewableCount = 0
		this.breadthFirstViewableId = null
		icons._transform = function (node, enc, next) {
			let existingNode = self.tree.get(node.id) || node
			// #20650 keep track of the first domain object within the org for the section
			existingNode[self.options.section] = node[self.options.section]
			existingNode.icon = node[self.options.section] ? 'circle-fill' : 'circle'
			this.push(existingNode)

			//capture the first viewable node id, used to determine if user can view beyond a single branch #19396
			if (self.options.permissions && _.get(node,'view', true)) {
				self.viewableCount++
				if (_.isNil(self.breadthFirstViewableId)) {
					self.breadthFirstViewableId = node.id
				}
			}

			next()
		}
		return icons
	},

	_treeOptions: function() {
		var treeOptions = Gardener.prototype._treeOptions.apply(this, arguments)
		treeOptions.stream = treeOptions.stream.pipe(this._iconTransform())
		treeOptions.stream = treeOptions.stream.pipe(pendingDeletionTransform(() => this.tree))
		treeOptions.accessors = { icon: 'icon' }
		treeOptions.droppable = moveRules.droppable
		treeOptions.movable = moveRules.movable

		return treeOptions
	},

	interested: function({domainType}) {
		return domainType === 'organization'
	},

	displayNoOrgs() {
		// Show the 'This project has no Organizations' message
		this.$('.tree-container tree').addClass('is-not-displayed')
		this.$('.tree-container').append(noOrgsViewable({
			message: i18n.get('common.project.hasNoOrg')
		}))
	},

	show: function(options = {}) {
		const {section:previousSection, projectId: previousProjectId} = this.options
		// important: must be before _renderTree
		this._updateOptions(options)

		// if we're already showing the tree but for a different section,
		// fill the org icons appropriately for the new section
		if (this.tree) {
			const newSection = options.section && options.section != previousSection
			const newProject = options.projectId && options.projectId != previousProjectId

			if (newProject) {
				// Can't patch org tree, we need an entirely new one for the new project
				this.reload()
			} else if (newSection) {
				// Patch the orgs for the new section
				const section = sections.toUrl(options.section)
				let projectId = options.projectId || projects.id
				if (_.isNil(projectId)) projectId = '' // #23372 avoid blowup
				var url = `api/organizations/${section}/defaults?projectId=${projectId}`
				var stream = new TreeStream({ url })
					.pipe(this._iconTransform())

				this.tree.edit(stream)
				var patched = []
				stream.on('data', function(node) {
					patched.push('' + node.id)
				})
				// fix the icons that weren't mentioned in the stream
				stream.on('end', () => {
					var all = Object.keys(this.tree.nodes)
					var difference = _.difference(all, patched)
					this.tree.edit(_.map(difference, (id) => {
						// the icons not mentioned are empty
						let existingNode = this.tree.get(id) || {}
						return _.assign(existingNode, { id: id, icon: 'circle' })
					}))
				})
			}
		} else {
			this._renderTree()
		}

		return this
	},

	_renderTree: function() {
		// On application start the menuManager renders the project and org navs
		// But if no project has yet been selected, the org gardener can't load itself
		// So we do it here the first time the tree is requested to be rendered. (#25537)
		if (!this.stream) {
			this._initializeStream(this.options.streamOptions)
		}
		Gardener.prototype._renderTree.apply(this, arguments)
	},

	move: function(node, newParent, oldParent, newIndex, oldIndex) {
		var undo = function () {
			this.tree.move(node.id, oldParent.id, oldIndex)
		}.bind(this)

		var pasteArgs = {
			mode: 'move',
			section: 'organizations',
			id: node.id,
			parentId: newParent.id,
			index: newIndex
		}

		paste(_.extend({}, pasteArgs, {
			error: function (xhr) {
				var response = xhr.responseJSON

				if (response.triggersCopyDeletion) {
					const confirmationView = orgHelper.copyDeletionDialog({
						node,
						response,
						tree: this.tree,
						confirmXHR: () => paste(_.extend({}, pasteArgs, {confirmed: true}))
					})
					// If the user cancels (doesn't confirm), undo the move in the tree
					this.listenTo(confirmationView, 'cancel', undo)
				} else {
					// Some other server-side error occurred; undo the move in the tree and
					// show the error dialog
					undo()
					appDisplay.dialog(new ErrorView(response))
				}
			}.bind(this)
		}))
	},

	_addDomainObject: function(model, relativeId) {
		// important: don't check interested because we need to fill in
		// org bubbles when something is added

		Gardener.prototype._addDomainObject.apply(this, arguments)

		if (model.get && model.get('organizationId')) {
			this._fill(model.section, model.get('organizationId'), model.id)
		}
	},

	_copyDomainObject: function(options, ids) {
		// important: don't check this.interested because we want to fill the
		// org icon when domain objects are copied in other sections (#12864)

		Gardener.prototype._copyDomainObject.apply(this, arguments)

		if (options.orgId) {
			var newId = ids[options.id]
			this._fill(options.section, options.orgId, newId)
		}
	},

	_deleteDomainObject: function(model) {
		if (!this.interested(model)) return
		this.updateTreeWithDeletedNode(model.attributes, this.tree)
	},

	/**
	 * This function handles updating the tree when one or more
	 * template copy nodes are deleted.  When this happens, the source nodes
	 * must be updated so they don't have attributes that cause them to be treated
	 * as templates.
	 *
	 * #22036, #21845
	 *
	 * @param {Object} node {id, templateId, templateRootId} among others
	 * @param tree
	 */
	updateTreeWithDeletedNode: function (node, tree) {

		if (!node) return

		const {id} = node
		const deletedNode = tree.get(id)
		// #37976 do nothing if deleted node doesn't exist in tree
		if (_.isNil(deletedNode)) return

		// edit the tree nodes for the copy options to stay up to date
		const updateNode = n => {
			if (n) {
				// If the source included children we need to unset the template attribute
				// for each of the descendants
				if (n.rootTemplateRootId) {
					tree.children(n).forEach(updateNode)
				}
				delete n.rootTemplateRootId
				delete n['isTemplate']
				tree.edit(n, {patch: false})
			}
		}

		// Reduces a list of nodes to a list of org ids where the org node is a template copy
		const templateIdReducer = (sourceIds, n) => {
			const sourceId = n.templateRootId || n.templateId
			if (sourceId) {
				sourceIds.push(sourceId)
			}
			return sourceIds
		}

		const templateCopies = _.filter([deletedNode, ...tree.children(deletedNode)],
			c => c.templateRootId || c.templateId)

		const sourcesOfDeletedNodes = _.reduce(templateCopies, templateIdReducer, [])

		// We have a list of all template copies being deleted, now delete them
		// so we can easily find any other copies from the same source(s)
		tree.removeNode(node)

		const sourcesRemainingInTree = _.values(tree.nodes).reduce(templateIdReducer,[])

		// If they don't exist elsewhere in the tree then update the sources
		const sourcesToUpdate = _.difference(sourcesOfDeletedNodes, sourcesRemainingInTree)
		_.each(sourcesToUpdate, id => updateNode(tree.get(id)))
	},

	// handles when the root of a tree changes.  This needs to be its own
	// event and not tacked onto move:domain-object, because move:domain-object
	// triggers behavior that isn't appropriate after a tree drag and drop
	// operation
	_changeRoot: function(options) {
		this._fill(options.section, options.orgId, options.id)

		// clear the cached reference to the root in its old location
		var org = this.tree.get(options.oldOrgId)
		org && delete org[options.section]
	},

	_fill: function(section, orgId, id) {
		if (section && section !== 'organizations') {
			var org = this.tree.get(orgId)
			// org will not be defined if the org tree has never been displayed
			// e.g. if the user went directly to an edit screen with a URL and
			// then started adding stuff
			if (org && !org[section]) {
				org[section] = id
				org.icon = 'circle-fill'
				this.tree.edit(org)
			}
		}
	},

	_unfill: function(section, orgId) {
		if (section && section !== 'organizations') {
			var org = this.tree.get(orgId)
			if (org) {
				delete org[section]
				org.icon = 'circle'
				this.tree.edit(org)
			}
		}
	},

	_loadOrgTree: function(orgTree) {
		_.each(orgTree, function(org) {
			this.tree.add(org, org.parentId)
		}.bind(this))
	},

	getAddType: function(relative, newItem) {
		// #7476 rule 4: If the selected item is a scorecard item or
		// organization, the new item is a child.
		return CHILD
	},

	getProjectId: function() {
		return projects.isVisible() ? this.options.projectId : null
	}
})

export default OrganizationsGardener

OrganizationsGardener.__filename__ = 'organizations/organizations-gardener.js'
